'use client';

import type { ReactNode } from 'react';
import { Container } from '@/atoms/Container/Container';
import { ResourceBookmarkButton } from './ResourceBookmarkButton';
import { ResourcePostButton } from './ResourcePostButton';
import { ResourceSavePicker } from './ResourceSavePicker';
import { ResourceTagBar, ResourceTagLabel } from './ResourceTagBar';

interface ResourceActionsProps {
  /** The external resource's link exactly as written: what is bookmarked, collected and tagged. */
  uri: string;
  /** How many public collections hold the link (Pinakes); absent for a link Pinakes has no card for. */
  collectionsCount?: number | null;
  /** The tag bar's label ("Tags on this video"). */
  tagsLabel: string;
  /**
   * The `Open in <service> ▾` control, for a card that has one: it takes the left of the action
   * row, and the tag label moves to the start of the tags below it.
   */
  openIn?: ReactNode;
}

/**
 * What closes an external resource card in a post: one zone under the card's one divider.
 *
 * - Row A: the Open in control (else the tag label) on the left; bookmark, add to collection with
 *   the public count, and post about this on the right. On a narrow screen the actions wrap under it.
 * - Row B: the tag label when Open in took its place, then the tags and "+".
 *
 * The zone is all controls, so a click on it never counts as a click through the card.
 */
export function ResourceActions({ uri, collectionsCount, tagsLabel, openIn }: ResourceActionsProps) {
  return (
    <Container
      overrideDefaults
      data-resource-controls
      data-testid="resource-actions"
      className="flex min-w-0 flex-col gap-2 px-4 py-3"
    >
      <Container
        overrideDefaults
        data-testid="resource-action-row"
        className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1"
      >
        {openIn ?? <ResourceTagLabel label={tagsLabel} />}
        <Container overrideDefaults className="ml-auto flex shrink-0 items-center gap-1">
          <ResourceBookmarkButton uri={uri} />
          <ResourceSavePicker uri={uri} collectionsCount={collectionsCount} />
          <ResourcePostButton uri={uri} />
        </Container>
      </Container>
      <ResourceTagBar uri={uri} label={openIn ? tagsLabel : undefined} />
    </Container>
  );
}
